import { Howl } from 'howler';
import { userSettingsStore } from '../../store/userSettingsStore';

const SoundType = {
  Music: 'music',
  Win: 'win',
  BetAccepted: 'betAccepted',
};

export class BaseSoundService {
  sounds = {
    [SoundType.Music]: null,
    [SoundType.Win]: null,
    [SoundType.BetAccepted]: null,
  };

  soundSourceIds = {
    [SoundType.Music]: null,
    [SoundType.Win]: null,
    [SoundType.BetAccepted]: null,
  };

  options = [];

  musicOptions = [];

  soundOptions = [];

  constructor({ options }) {
    this.options = [...options];

    this.options.forEach((option) => {
      if (option.name.includes(SoundType.Music)) {
        this.musicOptions.push(option);
      } else {
        this.soundOptions.push(option);
      }
    });
  }

  async loadResourceVersion2({ language }) {
    const max = this.options.length;
    let count = 1;
    return new Promise((resolve) => {
      this.options.forEach(({ name, getSrc, src, ...option }) => {
        if (typeof getSrc === 'function') {
          this.sounds[name] = new Howl({
            src: getSrc(language),
            ...option,
            onload: () => {
              count += 1;
              if (count === max) {
                resolve();
              }
            },
            onloaderror: () => {
              this.sounds[name] = new Howl({
                src,
                ...option,
                onload: () => {
                  count += 1;
                  if (count === max) {
                    resolve();
                  }
                },
              });
            },
          });
        } else {
          this.sounds[name] = new Howl({
            ...option,
            src,
          });

          this.sounds[name].once('load', () => {
            count += 1;
            if (count === max) {
              resolve();
            }
          });
        }
      });
    });
  }

  async loadResource({ language }) {
    const max = this.options.length;
    return new Promise((resolve, reject) => {
      if (!max) {
        reject();
      }
      this.options.forEach(({ name, getSrc, src, ...option }, index) => {
        if (typeof getSrc === 'function') {
          this.sounds[name] = new Howl({
            src: getSrc(language),
            ...option,
            onload: () => {
              if (index + 1 === max) {
                resolve();
              }
            },
            onloaderror: () => {
              if (index + 1 === max) {
                resolve();
              }
            },
          });
        } else {
          this.sounds[name] = new Howl({
            ...option,
            src,
            onloaderror: () => {
              if (index + 1 === max) {
                resolve();
              }
            },
            onload: () => {
              if (index + 1 === max) {
                resolve();
              }
            },
          });
        }
      });
    });
  }

  async init(initConfig) {
    await this.loadResource(initConfig);
  }

  async playWithoutStop(name, startTime = 0) {
    this.sounds[name].seek(startTime);
    this.soundSourceIds[name] = this.sounds[name].play();
  }

  async play(name, startTime = 0) {
    if (userSettingsStore.isSoundOn && userSettingsStore.soundValue) {
      await this.stop(name);
      return new Promise((resolve) => {
        if (!this.soundSourceIds[name]) {
          if (this.sounds[name]) {
            this.sounds[name].seek(startTime);
            this.sounds[name].on('end', resolve);
            this.soundSourceIds[name] = this.sounds[name].play();
          }
        }
      });
    }
    return new Promise((resolve) => {
      resolve();
    });
  }

  changeMusicVolume(volume) {
    this.musicOptions.forEach(({ name }) => {
      if (this.sounds[name]) {
        this.sounds[name].volume(volume);
      }
    });
  }

  changeSoundsVolume(volume) {
    this.soundOptions.forEach(({ name }) => {
      if (this.sounds[name]) {
        this.sounds[name].volume(volume);
      }
    });
  }

  async stop(name) {
    const id = this.soundSourceIds[name];
    if (id) {
      return new Promise((resolve) => {
        this.sounds[name].on('stop', () => resolve());
        this.sounds[name].stop(id);
        this.soundSourceIds[name] = null;
      });
    }
    return new Promise((res) => {
      res();
    });
  }

  mute(name) {
    if (this.sounds[name]) {
      this.sounds[name].mute(true);
    }
  }

  unMute(name) {
    if (this.sounds[name]) {
      this.sounds[name].mute(false);
    }
  }

  async playMusic() {
    await this.play(SoundType.Music);
  }

  async stopMusic() {
    await this.stop(SoundType.Music);
  }

  async playBetAccepted() {
    await this.play(SoundType.BetAccepted);
  }

  async stopBetAccepted() {
    await this.stop(SoundType.BetAccepted);
  }

  async playWin() {
    await this.play(SoundType.Win);
  }

  async stopWin() {
    await this.stop(SoundType.Win);
  }

  async stopAll() {
    await Promise.all(
      this.options.map(({ name }) => {
        if (!name.includes(SoundType.Music)) {
          return this.stop(name);
        }
        return Promise.resolve();
      })
    );
  }

  muteAll() {
    this.options.forEach(({ name }) => this.mute(name));
  }

  unMuteAll() {
    this.options.forEach(({ name }) => this.unMute(name));
  }

  muteMusic(isMute) {
    this.musicOptions.forEach(({ name }) => {
      if (this.sounds[name]) {
        this.sounds[name].mute(isMute);
      }
    });
  }

  muteSounds(isMute) {
    this.soundOptions.forEach(({ name }) => {
      if (this.sounds[name]) {
        this.sounds[name].mute(isMute);
      }
    });
  }
}
